import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { cn } from "~/utils";
import { ALL_MARKETS, useSalesData } from "~/contexts/sales-data-context";
import { applyMarketFocus } from "~/pages/product-details/tabs/sales-data-tab/data/apply-market-focus";
import { buildSummaryCardsModel } from "~/pages/product-details/tabs/sales-data-tab/data/build-summary-cards-model";
import { mapSalesResponse } from "~/pages/product-details/tabs/sales-data-tab/data/map-sales-response";
import { useAmazonSales } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-amazon-sales";
import { useMarketSales } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-market-sales";
import { AmazonConversionCard } from "./amazon-conversion-card";
import { AmazonRankingCard } from "./amazon-ranking-card";
import { TotalSalesCard } from "./total-sales-card";
import { TotalUnitsCard } from "./total-units-card";

export const SummaryCardsRow = ({
  isbns,
  bookTitle,
  onTotalCardClick,
  onAmazonCardClick,
  isPublished,
}: {
  isbns: string[] | undefined;
  bookTitle: string;
  onTotalCardClick: () => void;
  onAmazonCardClick: () => void;
  isPublished?: boolean;
}) => {
  const { t } = useTranslation(["productDetails"]);
  const {
    from,
    to,
    isBundled,
    markets,
    isFirstDatesLoading,
    marketFocus,
    setMarketFocus,
    publishedAt,
  } = useSalesData();

  // Bucketing is seeded to weekly so the chart's daily/monthly toggle does not
  // reshape card totals or sparklines; the cards never expose the setter.
  const { sales, isLoading, isError } = useMarketSales(isbns, "weekly");
  // One Amazon query feeds both the conversion and ranking cards.
  const {
    sales: amazon,
    isLoading: amazonLoading,
    isError: amazonError,
  } = useAmazonSales(isbns, "weekly");

  // A market can be offered by the toggle (it sold historically, per sales-info)
  // yet have no rows in the selected window; when the focused market is missing
  // from the windowed response, reset to All markets so the toggle and the
  // filtered view agree.
  useEffect(() => {
    if (
      marketFocus !== ALL_MARKETS &&
      sales &&
      !sales.markets.some((market) => market.market === marketFocus)
    ) {
      setMarketFocus(ALL_MARKETS);
    }
  }, [marketFocus, sales, setMarketFocus]);

  const data = useMemo(
    () =>
      sales
        ? buildSummaryCardsModel(
            applyMarketFocus(mapSalesResponse(sales, markets), marketFocus),
            markets,
          )
        : undefined,
    [sales, markets, marketFocus],
  );

  const noDataReason = t("salesData.summaryCards.unavailable.noData");
  const noRevenueReason = t("salesData.summaryCards.unavailable.noRevenue");
  const noRankingReason = t("salesData.summaryCards.amazonRanking.noHistory");

  const amazonMissing = amazonError || !amazon || amazon.buckets.length === 0;

  // No rank history (28% of ASINs) is a distinct empty state from "no Amazon
  // data": only a present response with a null bestRank gets the ranking-specific
  // note; a failed/empty query falls back to the generic no-data copy like the
  // other cards.
  const noRankHistory = !amazonMissing && amazon.summary.bestRank == null;
  const rankingUnavailable = amazonMissing || noRankHistory;
  const rankingReason = noRankHistory ? noRankingReason : noDataReason;

  const noData =
    isError ||
    !sales ||
    sales.markets.length === 0 ||
    sales.buckets.length === 0;

  const prePublicationReason =
    noData && publishedAt && !isPublished
      ? t("salesData.summaryCards.unavailable.notPublished", {
          title: bookTitle,
          date: format(publishedAt, "d MMMM yyyy"),
        })
      : undefined;
  const mediaControlNoDataReason = prePublicationReason ?? noDataReason;

  let salesUnavailableReason: string | undefined;
  if (noData) {
    salesUnavailableReason = mediaControlNoDataReason;
  } else if (data?.salesRevenueUnavailable) {
    salesUnavailableReason = noRevenueReason;
  }

  // The market-dimensioned cards order per-market values by the sales-info
  // market list, so keep them in their loading state until that lookup resolves
  // rather than render a provisional order and reshuffle a tick later.
  const marketCardsLoading = isLoading || isFirstDatesLoading;

  return (
    <div
      className={cn(
        "grid gap-4 md:grid-cols-2",
        isBundled ? "xl:grid-cols-3" : "xl:grid-cols-4",
      )}
      data-testid="summary-cards-row"
      aria-busy={marketCardsLoading || amazonLoading || undefined}
    >
      {(marketCardsLoading || amazonLoading) && (
        <span role="status" aria-live="polite" className="sr-only">
          {t("salesData.summaryCards.loading")}
        </span>
      )}
      <TotalUnitsCard
        isLoading={marketCardsLoading}
        units={noData ? undefined : data?.units}
        amazonSharePercent={
          amazonMissing
            ? null
            : (amazon?.summary?.amazonShareOfTotalUnits ?? null)
        }
        unavailableReason={noData ? mediaControlNoDataReason : undefined}
        onClick={onTotalCardClick}
      />
      <TotalSalesCard
        isLoading={marketCardsLoading}
        sales={noData ? undefined : data?.sales}
        from={from}
        to={to}
        unavailableReason={salesUnavailableReason}
        onClick={onTotalCardClick}
      />
      <AmazonConversionCard
        isLoading={amazonLoading}
        sales={amazonMissing ? undefined : amazon}
        unavailableReason={amazonMissing ? noDataReason : undefined}
        onClick={onAmazonCardClick}
      />
      {!isBundled && (
        <AmazonRankingCard
          isLoading={amazonLoading}
          sales={rankingUnavailable ? undefined : amazon}
          unavailableReason={rankingUnavailable ? rankingReason : undefined}
          onClick={onAmazonCardClick}
        />
      )}
    </div>
  );
};
